import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, MapPin } from 'lucide-react';

import Modal from '../common/Modal';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTime } from '../../utils/dateTime';
import { grnService } from '../../services/grnService';
import StorageReassignmentPicker, { ZonePlacement } from './StorageReassignmentPicker';

interface PastGrnUpdateModalsProps {
  updateId: number;
  action: 'view' | 'approve' | 'reject';
  onClose: () => void;
}

const FIELDS: { key: string; label: string; decimals?: number }[] = [
  { key: 'productDescription', label: 'Product' },
  { key: 'goodQty', label: 'Good Qty' },
  { key: 'cdamQty', label: 'Cosmetic Damage' },
  { key: 'damQty', label: 'Damage' },
  { key: 'palletCount', label: 'Pallets' },
  { key: 'itemCode', label: 'Item Code' },
  { key: 'unitType', label: 'Unit Type' },
  // GrnItem.weightPerItem/totalWeight are decimal(12,2) columns. The "before"
  // value comes back from the DB as a "210.00"-style string, but "after"
  // comes straight from the requester's submitted payload as a plain JS
  // number (210) — same value, different formatting, which both looked
  // like a spurious change (before/after strings didn't match) and showed
  // an inconsistent decimal count. Formatting both to a fixed 2 decimals
  // before comparing fixes both.
  { key: 'weightPerItem', label: 'Weight/Item (kg)', decimals: 2 },
  { key: 'totalWeight', label: 'Total Weight (kg)', decimals: 2 },
  { key: 'batchNo', label: 'Batch No' },
  { key: 'skuCode', label: 'SKU Code' },
  { key: 'customerSapCode', label: 'SAP Code' },
  { key: 'mfgDate', label: 'MFG Date' },
  { key: 'expiryDate', label: 'Expiry Date' },
  { key: 'remarks', label: 'Remarks' },
];

const formatFieldValue = (value: unknown, decimals?: number): unknown => {
  if (decimals == null || value === null || value === undefined || value === '') return value;
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(decimals) : value;
};

// Human-readable current-location chips from an enhanced GRN item. Driven by
// each placement's shape (zone vs rack/location) rather than storageType, so it
// works even when storageType is missing (e.g. imported GRNs).
const locationChips = (item: any): string[] => {
  if (!item) return [];
  const details = Array.isArray(item.storageDetails) ? item.storageDetails : [];
  const chips: string[] = [];
  for (const d of details) {
    if (!d || typeof d !== 'object') continue;
    if (d.zoneCode || d.zoneId != null) {
      // Prefer the specific zone-location slot code (e.g. "G8-03") over the
      // bare zone code when the pallet was assigned to one.
      const zone = d.zoneLocationCode || d.zoneCode || `Zone ${d.zoneId}`;
      chips.push(d.pallets ? `${zone} · ${d.pallets} plt` : zone);
    } else if (d.rackCode || d.locationCode || d.rackId != null || d.locationId != null) {
      const rack = d.rackCode || (d.rackId != null ? `Rack ${d.rackId}` : null);
      const loc = d.locationCode || (d.locationId != null ? `Loc ${d.locationId}` : null);
      const label = [rack, loc].filter(Boolean).join(' / ');
      if (label) chips.push(label);
    }
  }
  const lbd = Array.isArray(item.lbdStorageDetails) ? item.lbdStorageDetails : [];
  for (const d of lbd) {
    if (!d || typeof d !== 'object') continue;
    if (d.zoneCode || d.zoneId != null) {
      // Prefer the specific zone-location slot code (e.g. "ZONEALBD-01") over the
      // bare zone code, same as the non-LBD branch above — otherwise every LBD
      // pallet in the same zone collapses into one identical, indistinguishable chip.
      const zone = d.zoneLocationCode || d.zoneCode || `Zone ${d.zoneId}`;
      chips.push(`LBD · ${zone}${d.pallets ? ` · ${d.pallets} plt` : ''}`);
    } else if (d.rackCode || d.locationCode || d.rackId != null || d.locationId != null) {
      // Rack-based LBD placement (e.g. RACD racks) — only the location slot
      // (e.g. "RACD-007") is shown, not the rack code, since the rack is
      // redundant here (LBD locations are named after their rack already).
      const loc = d.locationCode || (d.locationId != null ? `Loc ${d.locationId}` : null);
      chips.push(`LBD · ${loc || 'LBD'}`);
    } else {
      chips.push('LBD · LBD');
    }
  }
  return chips;
};

// The requester's proposed placement is enhanced server-side (see
// past-grn-update.service.ts findOne, which runs it through the same
// enhanceStorageDetails/enhanceLbdStorageDetails used for currentItems) with
// zoneLocationCode/locationCode fields, so it resolves exactly like
// locationChips above — the actual slot, not just the zone/rack.
const proposedLocationChips = (
  storageDetails: Array<Record<string, any>> | null,
  includeRack: boolean = true,
): string[] => {
  if (!storageDetails) return [];
  const chips: string[] = [];
  for (const d of storageDetails) {
    if (!d || typeof d !== 'object') continue;
    if (d.zoneCode || d.zoneId != null) {
      const zone = d.zoneLocationCode || d.zoneCode || `Zone ${d.zoneId}`;
      chips.push(d.pallets ? `${zone} · ${d.pallets} plt` : zone);
    } else if (d.rackCode || d.locationCode || d.rackId != null || d.locationId != null) {
      const rack = includeRack ? d.rackCode || (d.rackId != null ? `Rack ${d.rackId}` : null) : null;
      const loc = d.locationCode || (d.locationId != null ? `Loc ${d.locationId}` : null);
      const label = [rack, loc].filter(Boolean).join(' / ');
      if (label) chips.push(label);
    }
  }
  return chips;
};

const PastGrnUpdateModals: React.FC<PastGrnUpdateModalsProps> = ({
  updateId,
  action,
  onClose,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState('');
  // Per-item "reassign storage" toggle + chosen zone/pallet placements, only
  // used in the approve view. Left untouched, an item's existing storage stays as-is.
  const [reassignEnabled, setReassignEnabled] = useState<Record<number, boolean>>({});
  const [reassignPlacements, setReassignPlacements] = useState<Record<number, ZonePlacement[]>>({});

  const { data: detail, isLoading } = useQuery({
    queryKey: ['past-grn-update', updateId],
    queryFn: () => grnService.getPastGrnUpdate(updateId),
    enabled: action === 'view' || action === 'approve',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['past-grn-updates'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
    // Reflect the applied edit immediately in the Inward report and GRN lists.
    queryClient.invalidateQueries({ queryKey: ['reports'] });
    queryClient.invalidateQueries({ queryKey: ['grns'] });
    queryClient.invalidateQueries({ queryKey: ['grn-items'] });
  };

  const approveMutation = useMutation({
    mutationFn: () => {
      const storageReassignments = Object.entries(reassignEnabled)
        .filter(([, enabled]) => enabled)
        .map(([grnItemId]) => ({
          grnItemId: Number(grnItemId),
          placements: reassignPlacements[Number(grnItemId)] || [],
        }));
      return grnService.approvePastGrnUpdate(
        updateId,
        storageReassignments.length ? storageReassignments : undefined,
      );
    },
    onSuccess: () => {
      showToast('Past GRN update approved and applied', 'success');
      invalidate();
      onClose();
    },
    onError: (error: any) =>
      showToast(error?.message || 'Failed to approve update', 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: () => grnService.rejectPastGrnUpdate(updateId, rejectionReason.trim()),
    onSuccess: () => {
      showToast('Past GRN update rejected', 'success');
      invalidate();
      onClose();
    },
    onError: (error: any) =>
      showToast(error?.message || 'Failed to reject update', 'error'),
  });

  const isMutating = approveMutation.isPending || rejectMutation.isPending;

  const handleConfirmApprove = () => {
    for (const [grnItemId, enabled] of Object.entries(reassignEnabled)) {
      if (!enabled) continue;
      const placements = reassignPlacements[Number(grnItemId)] || [];
      const total = placements.reduce((sum, p) => sum + (p.pallets || 0), 0);
      const item = itemsToShow.find((o) => o.grnItemId === Number(grnItemId));
      const required = item?.effectivePalletCount ?? 0;
      if (total !== required) {
        showToast(
          `Placed pallets (${total}) must equal the pallet count (${required}) for "${item?.productDescription}"`,
          'warning',
        );
        return;
      }
    }
    approveMutation.mutate();
  };

  const originalItems: any[] = detail?.originalSnapshot?.items ?? [];
  const proposedItems: any[] = detail?.proposedItems ?? [];
  const proposedById = new Map(proposedItems.map((item) => [item.grnItemId, item]));
  const currentItems: any[] = detail?.currentItems ?? [];
  const itemsById = new Map(currentItems.map((it: any) => [it.id, it]));

  // Precomputed per-item view used by both the read-only diff and the
  // approve-time reassignment picker.
  const itemsToShow = originalItems.map((orig) => {
    const proposed = proposedById.get(orig.grnItemId) || {};
    // A delete proposal only carries { action: 'delete', grnItemId } — every
    // other field falls back to `before` below, which used to render as a
    // misleading "No changes" badge instead of showing the item is being
    // removed entirely.
    const isDeleted = (proposed as any).action === 'delete';
    const rows = FIELDS.map((f) => {
      const before = formatFieldValue(orig[f.key], f.decimals);
      const after = formatFieldValue(
        proposed[f.key] !== undefined ? proposed[f.key] : orig[f.key],
        f.decimals,
      );
      return { ...f, before, after, changed: !isDeleted && String(before ?? '') !== String(after ?? '') };
    });
    return {
      grnItemId: orig.grnItemId,
      productDescription: orig.productDescription,
      rows,
      isDeleted,
      changedCount: rows.filter((r) => r.changed).length,
      chips: locationChips(itemsById.get(orig.grnItemId)),
      effectivePalletCount:
        proposed.palletCount !== undefined ? Number(proposed.palletCount) : Number(orig.palletCount ?? 0),
      palletCountChanged: rows.some((r) => r.key === 'palletCount' && r.changed),
      proposedStorage: Array.isArray((proposed as any).storageDetails) ? (proposed as any).storageDetails : null,
      proposedChips: proposedLocationChips(
        Array.isArray((proposed as any).storageDetails) ? (proposed as any).storageDetails : null,
      ),
      proposedLbdChips: proposedLocationChips(
        Array.isArray((proposed as any).lbdStorageDetails) ? (proposed as any).lbdStorageDetails : null,
        false,
      ),
    };
  });

  if (action === 'view' || action === 'approve') {
    const isApprove = action === 'approve';

    return (
      <Modal
        isOpen
        onClose={onClose}
        title={isApprove ? `Approve Past GRN Update — ${detail?.grnNumber || ''}` : `Past GRN Update — ${detail?.grnNumber || ''}`}
        size="xl"
        footer={
          isApprove ? (
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmApprove}
                disabled={isMutating || isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300"
              >
                {approveMutation.isPending ? 'Processing…' : 'Confirm Approval'}
              </button>
            </div>
          ) : undefined
        }
      >
        {isLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
            {/* Meta bar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="sm:col-span-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reason for edit</div>
                <div className="text-sm text-gray-900 mt-0.5">{detail?.editReason || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Requested by</div>
                <div className="text-sm text-gray-900 mt-0.5">{detail?.requestedByName || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Requested on</div>
                <div className="text-sm text-gray-900 mt-0.5">
                  {detail?.requestedAt ? formatDateTime(detail.requestedAt) : '—'}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">GRN</div>
                <div className="text-sm font-medium text-gray-900 mt-0.5">{detail?.grnNumber || '—'}</div>
              </div>
            </div>

            {isApprove && (
              <p className="text-sm text-gray-600">
                Approving applies the edited values to the GRN and re-syncs inventory and client
                stock. This cannot be undone. Storage placement is left as-is unless you turn on
                "Reassign storage" for an item below.
              </p>
            )}

            {/* Per-item comparison cards */}
            {itemsToShow.map((it) => (
              <div
                key={it.grnItemId}
                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              >
                {/* Card header */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900 truncate" title={it.productDescription}>
                    {it.productDescription}
                  </h4>
                  {it.isDeleted ? (
                    <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-700 border border-red-200">
                      Will be deleted
                    </span>
                  ) : it.changedCount > 0 ? (
                    <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                      {it.changedCount} change{it.changedCount !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                      No changes
                    </span>
                  )}
                </div>

                {it.isDeleted && (
                  <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 text-xs text-red-700">
                    This item will be permanently removed from the GRN — its inventory contribution
                    reversed and its storage released — if this request is approved.
                  </div>
                )}

                {/* Current location */}
                <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <MapPin className="w-3.5 h-3.5" /> Stored at
                  </span>
                  {it.chips.length > 0 ? (
                    it.chips.map((chip, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200"
                      >
                        {chip}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400 italic">Not assigned</span>
                  )}
                </div>

                {/* Requester's proposed new location, if any — shown in both
                    view and approve modes so the admin can see exactly where
                    the item would move to, not just a pallet/zone count. */}
                {it.proposedChips.length > 0 && (
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap bg-brand-50/40">
                    <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
                      <MapPin className="w-3.5 h-3.5" /> Requested move to
                    </span>
                    {it.proposedChips.map((chip, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 text-xs font-medium rounded-md bg-brand-100 text-brand-700 border border-brand-200"
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                )}

                {/* Requester's proposed new LBD (damaged-goods) location, if any. */}
                {it.proposedLbdChips.length > 0 && (
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap bg-amber-50/40">
                    <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                      <MapPin className="w-3.5 h-3.5" /> Requested LBD move to
                    </span>
                    {it.proposedLbdChips.map((chip, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 text-xs font-medium rounded-md bg-amber-100 text-amber-700 border border-amber-200"
                      >
                        LBD · {chip}
                      </span>
                    ))}
                  </div>
                )}

                {/* Field comparison */}
                <div className="divide-y divide-gray-50">
                  {it.rows.map((r) => (
                    <div
                      key={r.key}
                      className={`grid grid-cols-[150px_1fr_28px_1fr] items-center gap-2 px-4 py-2 text-sm ${
                        r.changed ? 'bg-amber-50/60' : ''
                      }`}
                    >
                      <span className="text-gray-500">{r.label}</span>
                      <span className={`truncate ${r.changed ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                        {String(r.before ?? '') === '' ? '—' : String(r.before)}
                      </span>
                      <span className="flex justify-center">
                        {r.changed && <ArrowRight className="w-4 h-4 text-amber-500" />}
                      </span>
                      <span className={`truncate font-medium ${r.changed ? 'text-amber-700' : 'text-gray-800'}`}>
                        {String(r.after ?? '') === '' ? '—' : String(r.after)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Approve-time storage reassignment — doesn't apply to an item being deleted */}
                {isApprove && !it.isDeleted && (
                  <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                    {it.proposedStorage && !reassignEnabled[it.grnItemId] && (
                      <p className="text-[11px] text-brand-700 mb-2">
                        Requester proposed a new placement ({it.proposedStorage.reduce((s: number, p: any) => s + (p.pallets || 0), 0)} pallets across {it.proposedStorage.length} zone{it.proposedStorage.length !== 1 ? 's' : ''}) — it will be applied automatically on approval unless you override below.
                      </p>
                    )}
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!reassignEnabled[it.grnItemId]}
                        onChange={(e) =>
                          setReassignEnabled((prev) => ({ ...prev, [it.grnItemId]: e.target.checked }))
                        }
                      />
                      {it.proposedStorage ? 'Override the proposed placement' : 'Reassign storage for this item'}
                      {it.palletCountChanged && (
                        <span className="text-amber-600">(pallet count changed — placement above is now stale)</span>
                      )}
                    </label>

                    {reassignEnabled[it.grnItemId] && detail?.warehouseId && (
                      <div className="mt-2">
                        <StorageReassignmentPicker
                          warehouseId={detail.warehouseId}
                          requiredPallets={it.effectivePalletCount}
                          value={reassignPlacements[it.grnItemId] || []}
                          onChange={(placements) =>
                            setReassignPlacements((prev) => ({ ...prev, [it.grnItemId]: placements }))
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Reject Past GRN Update"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!rejectionReason.trim()) {
                showToast('Please provide a rejection reason', 'warning');
                return;
              }
              rejectMutation.mutate();
            }}
            disabled={isMutating}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300"
          >
            {isMutating ? 'Processing…' : 'Confirm Rejection'}
          </button>
        </div>
      }
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Rejection reason <span className="text-red-500">*</span>
        </label>
        <textarea
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500"
          placeholder="Explain why this update is being rejected"
        />
      </div>
    </Modal>
  );
};

export default PastGrnUpdateModals;
